import { useEffect, useState } from "react";
import { useTranslation } from "../i18n";
import { motion, AnimatePresence } from "framer-motion";
import { RiCloseLine, RiCheckLine, RiDeleteBinLine } from "react-icons/ri";
import type { Job } from "../types/job";
import { jobErrorText } from "../lib/jobError";
import { formatModel, jobDisplayName, statusText } from "../lib/jobDisplay";

interface ImageGenerationMultiSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: Job;
  onApprove: (jobId: Job) => Promise<void>;
  onDecline: (jobId: string) => Promise<void>;
  onDismiss?: (jobId: string) => Promise<void>;
  showHistory?: boolean;
}

interface GeneratedImage {
  url: string;
  fileSize?: number;
  index: number;
}

export default function ImageGenerationMultiSelectModal({
  isOpen,
  onClose,
  job,
  onApprove,
  onDecline,
  onDismiss,
  showHistory = false,
}: ImageGenerationMultiSelectModalProps) {
  const { t, i18n } = useTranslation();
  const [isDeclining, setIsDeclining] = useState(false);
  const [approvingIndices, setApprovingIndices] = useState<Set<number>>(new Set());

  const generatedImages: GeneratedImage[] = job.metadata?.generatedImages || [];

  // Esc closes the modal (same callback as the X). Only attached while mounted.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const handleDecline = async () => {
    try {
      setIsDeclining(true);
      await onDecline(job.id);
      onClose();
    } finally {
      setIsDeclining(false);
    }
  };

  const handleDismiss = async () => {
    if (onDismiss) {
      await onDismiss(job.id);
      onClose();
    }
  };

  const handleApproveImage = async (imageIndex: number) => {
    try {
      setApprovingIndices((prev) => new Set(prev).add(imageIndex));

      // Create a modified job with only the selected image
      const modifiedJob = {
        ...job,
        resultUrl: generatedImages[imageIndex].url,
        metadata: {
          ...job.metadata,
          selectedImageIndex: imageIndex,
        },
      };

      await onApprove(modifiedJob);

      // If successful, close the modal
      onClose();
    } catch (error) {
      console.error("Error approving image:", error);
    } finally {
      setApprovingIndices((prev) => {
        const next = new Set(prev);
        next.delete(imageIndex);
        return next;
      });
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-60"
            onClick={onClose}
          />

          <motion.div
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 420, damping: 32 }}
            className="fixed top-16 right-4 bottom-4 w-[520px] max-w-[90vw] rounded-lg border border-white/20 bg-white shadow-2xl z-70 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-fuchsia-500/5 pointer-events-none" />

            <div className="relative h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-black/5">
                <div className="space-y-0.5">
                  <p className="text-xs tracking-[0.08em] text-gray-500">
                    {t("modal.jobPreviewUpper")}
                  </p>
                  <h2 className="text-lg font-semibold text-gray-800">
                    {jobDisplayName(job, t, i18n)}
                  </h2>
                  <p className="text-xs text-gray-500">
                    {generatedImages.length} {t("modal.generatedImages")}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {onDismiss && !showHistory && (
                    <button
                      onClick={handleDismiss}
                      className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                      title={t("actions.dismiss")}
                    >
                      <RiDeleteBinLine className="w-4 h-4 text-gray-600 hover:text-red-600" />
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="w-11 h-11 flex items-center justify-center hover:bg-black/5 rounded-lg transition-colors"
                  >
                    <RiCloseLine className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Prompt */}
                {job.prompt && (
                  <div className="border border-gray-200 rounded-lg p-3 bg-white text-sm">
                    <p className="text-gray-500 text-[11px] tracking-[0.08em] mb-1">
                      {t("modal.promptUpper")}
                    </p>
                    <div className="p-2 rounded-md bg-gray-50 border border-gray-100 max-h-28 overflow-y-auto text-xs text-gray-700 leading-snug whitespace-pre-wrap">
                      {job.prompt}
                    </div>
                  </div>
                )}

                {/* Source Image */}
                {job.sourceUrl && (
                  <div>
                    <h3 className="text-[11px] font-semibold text-gray-600 uppercase tracking-[0.08em] mb-2">
                      {t("modal.before")}
                    </h3>
                    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                      <div className="p-3">
                        <img
                          src={job.sourceUrl}
                          alt={t("modal.before")}
                          className="w-full h-auto rounded-md object-contain bg-gray-50"
                          style={{ maxHeight: "300px" }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Generated Images Grid */}
                <div>
                  <h3 className="text-[11px] font-semibold text-gray-600 uppercase tracking-[0.08em] mb-3">
                    {t("modal.generatedImagesUpper")}
                  </h3>
                  <div className="space-y-3">
                    {generatedImages.map((image, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm"
                      >
                        <div className="px-3 py-2 text-xs font-semibold text-gray-600 border-b border-gray-100">
                          {t("modal.imageNumber", { number: index + 1 })}
                        </div>
                        <div className="p-3">
                          <img
                            src={image.url}
                            alt={`Generated ${index + 1}`}
                            className="w-full h-auto rounded-md object-contain bg-gray-50"
                            style={{ maxHeight: "300px" }}
                          />
                        </div>
                        {!showHistory && job.status === "inspect" && (
                          <div className="px-3 pb-3">
                            <button
                              onClick={() => handleApproveImage(index)}
                              disabled={approvingIndices.has(index)}
                              className="w-full flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                            >
                              <RiCheckLine className="w-4 h-4" />
                              {approvingIndices.has(index)
                                ? t("actions.approving")
                                : t("actions.approve")}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {job.error && (
                  <div className="border border-red-200 rounded-lg p-3 bg-red-50 text-sm">
                    <p className="text-red-700 text-[11px] uppercase tracking-[0.08em] mb-1">
                      {t("jobCard.error")}
                    </p>
                    <p className="text-red-700 text-xs leading-snug">{jobErrorText(job, t)}</p>
                  </div>
                )}

                {/* Parameters */}
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500 text-[11px] uppercase tracking-[0.08em]">
                      {t("jobCard.model")}
                    </p>
                    <p className="text-gray-800 font-semibold break-words">
                      {formatModel(job.model, t) || t("status.unknown")}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-[11px] uppercase tracking-[0.08em]">
                      {t("jobCard.created")}
                    </p>
                    <p className="text-gray-800 font-semibold">
                      {new Date(job.createdAt).toLocaleDateString(
                        i18n.language === "tr" ? "tr-TR" : "en-US"
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-[11px] uppercase tracking-[0.08em]">
                      {t("jobCard.status")}
                    </p>
                    <p className="text-gray-800 font-semibold capitalize">
                      {statusText(job.status, t, i18n)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer - Only Decline and Dismiss */}
              {!showHistory && (
                <div className="border-t border-gray-200 px-4 py-3 flex gap-2 bg-white">
                  <button
                    onClick={handleDecline}
                    disabled={isDeclining}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-white hover:bg-red-50 text-red-600 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-red-200"
                  >
                    {isDeclining ? t("actions.declining") : t("actions.decline")}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
